'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["livePreviewId", "pageUrl", "response", "windowWidth"],
  _excluded2 = ["windowWidth"];
import I18n from 'I18n';
import classNames from 'classnames';
import * as DeviceTypes from 'feedback-schema/constants/devices';
import * as MessageChannels from 'feedback-schema/constants/messageChannels';
import * as SurveyStages from 'feedback-schema/constants/surveyStages';
import * as PopupStyles from 'feedback-schema/constants/surveyProperties/popupStyles';
import * as MessageTypes from 'feedback-schema/constants/messageTypes';
import getDeviceType from 'feedback-utils/getDeviceType';
import { parentSender } from 'feedback-utils/messages';
import { parentListener } from 'feedback-utils/subscriptions/messages';
import intervalAggregator from 'feedback-utils/subscriptions/interval';
import { isWebCalloutStyle } from 'feedback-utils/webStyle';
import { getRespondentType } from 'feedback-utils/respondentTypes';
import { Record } from 'immutable';
import { Component } from 'react';
import { NavMarker } from 'react-rhumb';
import bestContrastColor from 'feedback-utils/bestContrastColor';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import FollowUp from '../components/pages/FollowUp';
import NoSurvey from '../components/pages/NoSurvey';
import Question from '../components/pages/Question';
import ThankYou from '../components/pages/ThankYou';
import CloseButton from 'feedback-web-renderer-ui/components/CloseButton';
import { SurveyTypeEnum } from '../constants/surveyTypes';
import { WEB_CHANNEL, LegalConsentOptions, Routes } from '../Constants';
import * as Modes from '../constants/modes';
import WebRespondent from '../records/WebRespondentRecord';
import * as Api from '../utils/api';
import formatContactProperties from '../utils/formatContactProperties';
import '../utils/setupErrorReporting';
import { fingerprintedReporter } from '../utils/errorReporter';
import { handleIgnoreSurvey, isSurveyIgnored } from '../utils/ignoreSurvey';
import toConfigRecord from '../utils/toConfigRecord';
import Callout from '../components/pages/Callout';
import { getWidthForStage } from '../utils/size';
import ErrorPage from '../components/pages/Error';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const MAX_HEIGHT = 350;
const sendToParent = parentSender(MessageChannels.UI);
const showPopup = payload => {
  sendToParent(MessageTypes.SHOW, payload);
};
const hidePopup = () => {
  sendToParent(MessageTypes.HIDE);
};
const readyForMessages = () => {
  sendToParent(MessageTypes.READY);
};
const expandPopup = expand => {
  sendToParent(MessageTypes.EXPAND, expand);
};
const resizePopup = (newHeight, newWidth) => {
  sendToParent(MessageTypes.RESIZE_POPUP, {
    newHeight,
    newWidth
  });
};
const changeStage = (stage, rating) => {
  sendToParent(MessageTypes.STAGE_CHANGE, {
    stage,
    rating
  });
};
const getClasses = (config, alwaysDisplayMobile = true, stage, ...aditionalClasses) => [...config.embedStyle.split('_'), ...(stage !== SurveyStages.CALLOUT ? ['expanded'] : []), config.surveyType, ...(!config.displayOnMobile && !alwaysDisplayMobile ? ['no-mobile'] : ['']), ...aditionalClasses].filter(Boolean).map(className => `hs-feedback-${className}`);
class FeedbackForm extends Component {
  constructor(props) {
    super(props);
    this.maybeResizePopup = () => {
      const {
        config,
        isPreview,
        stage
      } = this.state;
      const newHeight = Math.min(this.containerNode.scrollHeight, MAX_HEIGHT);
      const newWidth = getWidthForStage(stage, config, this.containerNode, isPreview);

      // hacky 2px threshold fixes a vibrating-nps bug in firefox
      if (Math.abs(this.popupHeight - newHeight) > 2 || Math.abs(this.popupWidth - newWidth) > 2) {
        this.popupHeight = newHeight;
        this.popupWidth = newWidth;
        resizePopup(newHeight, newWidth);
        setTimeout(this.maybeResizePopup, 200);
      }
    };
    this.handleExpandQuestion = () => {
      this.setState({
        stage: SurveyStages.QUESTION
      });
      expandPopup(true);
      changeStage(SurveyStages.QUESTION);
    };
    this.handlePreviewMessage = (messageType, {
      config: messageConfig,
      contactProperties,
      data,
      deviceType,
      interactionDisabled,
      state: stage,
      surveyType
    }) => {
      const selectedRating = data && typeof data.selectedFeedbackRatingValue === 'number' ? data.selectedFeedbackRatingValue : this.state.selectedRating;
      switch (messageType) {
        case MessageTypes.SETUP:
          {
            const config = toConfigRecord(messageConfig);
            this.setState({
              config,
              deviceType: deviceType || DeviceTypes.DESKTOP,
              interactionDisabled,
              stage,
              selectedRating,
              webRespondent: new WebRespondent({
                hasEmail: !config.gdprSettings
              }),
              contactProperties: formatContactProperties(contactProperties)
            });
            showPopup({
              classes: getClasses(config.merge({
                surveyType
              }), true, stage, deviceType)
            });
            break;
          }
        case MessageTypes.CHANGE_STATE:
          {
            this.setState({
              stage,
              selectedRating
            });
            expandPopup(stage !== SurveyStages.CALLOUT);
            break;
          }
        case MessageTypes.UPDATE_PREVIEW:
          {
            const config = toConfigRecord(messageConfig);
            const currentStyle = this.state.config ? this.state.config.embedStyle : '';
            let nextStage;
            if (currentStyle !== config.embedStyle) {
              nextStage = isWebCalloutStyle(config.embedStyle) ? SurveyStages.CALLOUT : SurveyStages.QUESTION;
              showPopup({
                classes: getClasses(config, true, nextStage, deviceType)
              });
            } else {
              nextStage = this.state.stage;
            }
            this.setState({
              config,
              nextStage
            });
            break;
          }
        default:
          break;
      }
    };
    this.handleResponse = response => {
      if (!response || !response.config || this.state.config) {
        return;
      }
      const {
        config,
        webRespondent
      } = response;
      const {
        portalId,
        isTest,
        isPreview
      } = this.state;
      const mappedConfig = toConfigRecord(config);
      const {
        surveyId,
        embedStyle,
        displayDelay,
        surveyType
      } = mappedConfig;
      if (!(isTest || isPreview) && isSurveyIgnored(surveyType, surveyId, portalId, mappedConfig.surveyFrequencyType)) {
        return;
      }
      const isCallout = isWebCalloutStyle(embedStyle);
      const stage = isCallout ? SurveyStages.CALLOUT : SurveyStages.QUESTION;
      this.setState({
        config: mappedConfig,
        stage,
        webRespondent: new WebRespondent(webRespondent)
      });
      window.setTimeout(() => {
        showPopup({
          classes: getClasses(mappedConfig, false, stage, this.state.deviceType),
          surveyId,
          displayOnScroll: config.displayOnScroll,
          surveyLanguage: config.language,
          surveyType: SurveyTypeEnum[surveyType],
          userLocale: I18n.locale
        });
      }, displayDelay * 1000);
    };
    this.handleSetup = ({
      mode,
      payload
    }) => {
      if (!payload) {
        return;
      }
      const {
          livePreviewId,
          pageUrl,
          response,
          windowWidth
        } = payload,
        rest = _objectWithoutPropertiesLoose(payload, _excluded);
      const deviceType = getDeviceType(windowWidth);
      this.setState(Object.assign({
        deviceType,
        mode,
        pageUrl
      }, rest), () => {
        switch (mode) {
          case Modes.STANDARD:
            this.handleResponse(response);
            return;
          case Modes.LIVE_PREVIEW:
            Api.fetchLiveTestSurveyConfig(livePreviewId, pageUrl).then(this.handleResponse).catch(() => {
              console.warn('HubSpot Feedback Survey Preview: Preview id is either expired or does not exist');
            });
            return;
          default:
            break;
        }
      });
    };
    this.handleUIMessage = (messageType, payload) => {
      switch (messageType) {
        case MessageTypes.SETUP:
          this.handleSetup(payload);
          break;
        case MessageTypes.READY:
          sendToParent(MessageTypes.SETUP, {
            mode: Modes.STANDARD,
            payload: {
              pageTitle: 'Hello',
              pageUrl: window.location.href,
              windowWidth: window.innerWidth
            }
          });
          window.addEventListener('resize', this.maybeResizePopup);
          break;
        case MessageTypes.UPDATE:
          {
            const {
                windowWidth
              } = payload,
              rest = _objectWithoutPropertiesLoose(payload, _excluded2);
            this.setState(Object.assign({
              deviceType: getDeviceType(windowWidth)
            }, rest));
            break;
          }
        case MessageTypes.RESET:
          {
            this.handleHidePopup();
            break;
          }
        case MessageTypes.FOCUS:
          this.hideIfSubmitted();
          break;
        case MessageTypes.CHANGE_EXPANDED_STATE:
          {
            const {
              stage
            } = this.state;
            if ([SurveyStages.QUESTION, SurveyStages.CALLOUT].includes(stage)) {
              this.setState({
                stage: payload ? SurveyStages.QUESTION : SurveyStages.CALLOUT
              });
            }
            break;
          }
        default:
          break;
      }
    };
    this.handleRatingSelect = rating => {
      const {
        config: {
          backoff,
          backoffTimeUnit,
          surveyFrequencyType,
          surveyType,
          surveyId
        },
        isHubspot,
        interactionDisabled,
        isTest,
        isPreview,
        pageTitle,
        pageUrl,
        portalId,
        utk,
        webRespondent: {
          contactId
        }
      } = this.state;
      if (!interactionDisabled) {
        this.setState({
          selectedRating: rating,
          stage: SurveyStages.FOLLOW_UP
        });
        changeStage(SurveyStages.FOLLOW_UP, rating);
      }
      if (!(isTest || isPreview)) {
        Api.submitRating(surveyType, {
          channel: WEB_CHANNEL,
          contactId,
          isHubspot,
          portalId,
          surveyChannel: WEB_CHANNEL,
          surveyId,
          utk,
          value: rating,
          webAnalytics: {
            pageTitle,
            pageUrl,
            userAgent: navigator.userAgent
          }
        }).then(({
          submissionId
        }) => {
          this.setState({
            submissionId
          });
        }).catch(e => {
          this.setState({
            stage: SurveyStages.ERROR
          });
          fingerprintedReporter(['submit rating'], e);
        });
        this.thisFrameSubmitted[JSON.stringify([surveyType, surveyId, portalId])] = true;
        handleIgnoreSurvey({
          surveyType,
          surveyId,
          portalId,
          surveyFrequencyType,
          backoff,
          backoffTimeUnit,
          isPreview,
          isTest
        });
      }
    };
    this.handleFollowUpSubmit = followUp => {
      const {
        config: {
          surveyId,
          surveyType
        },
        isHubspot,
        interactionDisabled,
        isPreview,
        isTest,
        pageUrl,
        portalId,
        selectedRating,
        submissionId
      } = this.state;
      if (!interactionDisabled) {
        this.setState({
          stage: SurveyStages.THANK_YOU
        });
        changeStage(SurveyStages.THANK_YOU);
      }
      if (!(isTest || isPreview)) {
        Api.submitFollowUp(surveyType, pageUrl, {
          followUp,
          isHubspot,
          portalId,
          rating: selectedRating,
          submissionId,
          surveyId
        }).catch(fingerprintedReporter(['submit follow up']));
      }
    };
    this.handleEmailSubmit = (email, consentsToReceiveCommunications) => {
      const {
        config: {
          gdprSettings,
          surveyId,
          surveyType
        },
        interactionDisabled,
        isHubspot,
        isPreview,
        isTest,
        pageUrl,
        portalId,
        submissionId,
        utk
      } = this.state;
      if (!interactionDisabled) {
        this.handleHidePopup();
      }
      if (!(isTest || isPreview)) {
        Api.submitEmail(pageUrl, Object.assign({
          email,
          isHubspot,
          surveyType,
          portalId,
          submissionId,
          surveyId
        }, gdprSettings && gdprSettings.gdprConsentType !== LegalConsentOptions.NONE ? {
          gdprConsent: {
            gdprConsentType: gdprSettings.gdprConsentType,
            legitimateInterestLawfulBasis: gdprSettings.legitimateInterestLawfulBasis,
            consentsToReceiveCommunications: consentsToReceiveCommunications || gdprSettings.gdprConsentType !== LegalConsentOptions.SEPARATE_CONSENT
          }
        } : {}, {
          utk
        })).catch(fingerprintedReporter(['submit email']));
      }
    };
    this.handleHidePopup = () => {
      hidePopup();
      setTimeout(() => {
        this.setState({
          config: null
        });
      }, 1000);
    };
    this.hideIfSubmitted = () => {
      const {
        config,
        mode,
        portalId
      } = this.state;
      if (!(config && mode === Modes.STANDARD)) return;
      const {
        surveyId,
        surveyType
      } = config;
      if (!this.thisFrameSubmitted[JSON.stringify([surveyType, surveyId, portalId])] && isSurveyIgnored(surveyType, surveyId, portalId, config.surveyFrequencyType)) {
        this.handleHidePopup();
      }
    };
    this.renderPopupContent = () => {
      const {
        config,
        contactProperties,
        deviceType,
        isPreview,
        isTest,
        mode,
        selectedRating,
        submissionId,
        stage,
        webRespondent
      } = this.state;
      const respondentType = !isNaN(selectedRating) && getRespondentType(config.surveyType, selectedRating);
      switch (stage) {
        case SurveyStages.LOADING:
          return /*#__PURE__*/_jsx(LoadingSpinner, {
            grow: true
          });
        case SurveyStages.CALLOUT:
          return /*#__PURE__*/_jsx(Callout, {
            config: config,
            contactProperties: contactProperties,
            onExpand: this.handleExpandQuestion,
            route: Routes.CALLOUT
          });
        case SurveyStages.QUESTION:
          return /*#__PURE__*/_jsx(Question, {
            config: config,
            contactProperties: contactProperties,
            deviceType: deviceType,
            mode: mode,
            onRatingSelect: this.handleRatingSelect,
            route: Routes.QUESTION,
            surveyType: config.surveyType
          });
        case SurveyStages.FOLLOW_UP:
          return /*#__PURE__*/_jsx(FollowUp, {
            autoFocus: !isPreview,
            config: config,
            mode: mode,
            onSubmitClick: this.handleFollowUpSubmit,
            respondentType: respondentType,
            route: Routes.FOLLOW_UP,
            submitButtonDisabled: !(isTest || isPreview || submissionId)
          });
        case SurveyStages.THANK_YOU:
          return /*#__PURE__*/_jsx(ThankYou, {
            config: config,
            mode: mode,
            onClose: isPreview ? () => null : this.handleHidePopup,
            onImageLoad: this.maybeResizePopup,
            onSubmitClick: this.handleEmailSubmit,
            respondentType: respondentType,
            route: Routes.THANK_YOU,
            webRespondent: webRespondent
          });
        case SurveyStages.ERROR:
          return /*#__PURE__*/_jsx(ErrorPage, {
            onClose: this.handleHidePopup
          });
        default:
          return /*#__PURE__*/_jsx("div", {});
      }
    };
    this.state = {
      config: undefined,
      contactProperties: Record({})(),
      deviceType: undefined,
      interactionDisabled: false,
      isHubspot: false,
      isPreview: false,
      isTest: false,
      mode: undefined,
      pageTitle: undefined,
      pageUrl: undefined,
      selectedRating: undefined,
      stage: SurveyStages.LOADING,
      submissionId: undefined,
      webRespondent: new WebRespondent(),
      utk: undefined
    };
    window.addEventListener('resize', this.maybeResizePopup);
    this.popupHeight = 0;
    this.popupWidth = 0;
    this.containerNode = document.getElementById('servicehub-feedback');
    this.thisFrameSubmitted = {};
    parentListener(MessageChannels.UI)(this.handleUIMessage);
    parentListener(MessageChannels.PREVIEW_UI)(this.handlePreviewMessage);
    intervalAggregator(1000)(this.hideIfSubmitted);
    readyForMessages();
  }
  componentDidUpdate(__, prevState) {
    const {
      updateRoute
    } = this.props;
    const {
      stage: oldStage
    } = prevState;
    const {
      stage
    } = this.state;
    if (stage !== oldStage) {
      updateRoute(`/${stage.toLowerCase()}`);
    }
    this.maybeResizePopup();
  }
  renderInternal() {
    const {
      config,
      deviceType,
      isTest,
      isPreview,
      portalId,
      stage
    } = this.state;
    if (!config) {
      return /*#__PURE__*/_jsx(NoSurvey, {
        route: Routes.NO_SURVEY
      });
    }
    const {
      themeColor,
      surveyId,
      surveyFrequencyType,
      surveyType
    } = config;
    const closeButtonColor = bestContrastColor(themeColor);
    return /*#__PURE__*/_jsxs("div", {
      className: classNames('height-100', config.embedStyle.toLowerCase()),
      id: "form-wrapper",
      children: [/*#__PURE__*/_jsx("div", {
        className: classNames('page', stage.toLowerCase(), config.surveyType.toLowerCase(), (config.embedStyle === PopupStyles.POPUP_TOP || deviceType !== DeviceTypes.DESKTOP) && stage !== SurveyStages.CALLOUT && 'width-100', stage === SurveyStages.CALLOUT && "minimized"),
        children: this.renderPopupContent()
      }), /*#__PURE__*/_jsx(CloseButton, {
        stage: stage,
        isPreview: isPreview,
        isTest: isTest,
        surveyType: surveyType,
        surveyId: surveyId,
        portalId: portalId,
        surveyFrequencyType: surveyFrequencyType,
        closeButtonColor: closeButtonColor,
        onClose: this.handleHidePopup
      })]
    });
  }
  render() {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "root",
      children: this.renderInternal()
    });
  }
}
export default FeedbackForm;